/**
 * AccountSettingsView — current-user profile + organization switcher.
 *
 * S-073 / FEAT-006 (Phase 9). The final admin screen. Three sections:
 *
 *   1. Profile — Clerk's `<UserProfile>` component handles email,
 *      password, MFA, sessions, and connected accounts. We get the
 *      full Clerk-hosted UX with zero custom code.
 *
 *   2. Organization — Clerk's `<OrganizationSwitcher>` so users with
 *      multi-org Clerk accounts can flip between agencies. Each Clerk
 *      org maps to one CCM tenant via `publicMetadata.tenant_db_id`.
 *      Switching here changes the org claim on the JWT; the
 *      RequireTenant gate picks up the new scope on the next nav.
 *
 *   3. Sign out — a button that calls Clerk's `signOut()` and
 *      navigates to /login. Clerk also exposes sign-out from the
 *      `<UserProfile>` flyout, but a top-level button is easier to
 *      reach.
 *
 * S-082 / FEAT-006 (Phase 9) — only-admin orphan prevention.
 *
 *   Clerk's `<OrganizationProfile>` widget (rendered when the user
 *   clicks "Manage" inside `<OrganizationSwitcher>`) exposes two
 *   destructive actions on the General page:
 *     - Leave organization
 *     - Delete organization
 *
 *   Clerk's own gate allows those whenever there's another admin OR a
 *   pending admin invitation. Pending invitations can sit unaccepted
 *   forever, so a user could leave/delete and orphan the tenant
 *   (Leave → DB row points at a Clerk org with no admins; Delete →
 *   `tenants.clerk_org_id` references a dead Clerk org).
 *
 *   S-081 already guards our own `/memberships` endpoints, but Clerk's
 *   widget hits Clerk's API directly and bypasses our backend. The
 *   only place we can intervene is the SPA.
 *
 *   Defense:
 *     1. Count active admins on the current active workspace via
 *        `GET /memberships?agencyclient_id=<active-id>`.
 *     2. If the caller is the only admin (and pending invitations are
 *        NOT counted), inject a `<style>` rule that hides Clerk's
 *        leave/delete sections and render a banner above the widget
 *        pointing to /settings/team.
 *
 *   Residual gap (documented in the spec's Open Concerns): a
 *   determined attacker with DevTools can still remove the injected
 *   `<style>` rule and click through, OR call Clerk's API directly via
 *   fetch. S-082 is accidental-orphan prevention. Webhook-based
 *   reaction (Option 3 in the spec) is the next hardening pass.
 */

import { useMemo } from "react";
import {
  OrganizationSwitcher,
  UserProfile,
  useClerk,
  useOrganization,
} from "@clerk/clerk-react";
import { Link, useNavigate } from "react-router-dom";
import { useApiQuery } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import {
  DATE_FORMAT_LABELS,
  DATE_FORMAT_SETTINGS,
  DEFAULT_DATE_FORMAT,
  formatDateTime,
} from "@/lib/date-format";
import { useDateFormat, useSetDateFormat } from "@/lib/useDateFormat";

// FEAT-040 / S-252 — tenant type badge. Read-only everywhere; editing lives
// in the platform-admin console (/admin/tenants), not here.
interface TenantMe {
  tenant_type?: string;
}

const TENANT_TYPE_LABELS: Record<string, string> = {
  admin: "Admin",
  internal: "Internal",
  agency: "Agency",
  marketing: "Marketing",
  solowriter: "Solo Writer",
  leader: "Leader",
};

/**
 * S-082 — hidden CSS rules targeting Clerk's danger sections.
 *
 * DOM inspection on Clerk v5.x (2026-05-26) shows the Leave and Delete
 * sections share the `cl-profileSection__organizationDanger` modifier
 * class. Targeting the section wrapper hides both buttons + their
 * headers in one rule.
 *
 * Defense in depth: also target by `data-localization-key` so a future
 * Clerk minor that renames the class still hides the buttons via the
 * stable i18n key. The Clerk widget renders in a portal at document
 * body, so global CSS scope reaches it regardless of where this style
 * tag is mounted in the React tree.
 */
const HIDE_DANGER_CSS = `
  .cl-profileSection__organizationDanger,
  [data-localization-key="organizationProfile.profilePage.dangerSection.leaveOrganization.title"],
  [data-localization-key="organizationProfile.profilePage.dangerSection.deleteOrganization.title"] {
    display: none !important;
  }
`;

export function AccountSettingsView() {
  const clerk = useClerk();
  const navigate = useNavigate();
  const { data: tenantMe } = useApiQuery<TenantMe>(["tenants", "me"], "/tenants/me");

  // S-082 — gate the Leave/Delete affordances using CLERK's view of the
  // active organization. The previous version queried our /memberships
  // endpoint scoped to publicMetadata.active_agencyclient_id, but that
  // is a CCM-workspace concept; Clerk's Leave/Delete buttons operate on
  // the Clerk org being managed, which may not match the user's stored
  // active workspace (e.g., user has multiple Clerk orgs and is viewing
  // a different one in OrganizationSwitcher). `useOrganization()` always
  // reflects the org currently being managed.
  //
  // `organization.membersCount` is the count of accepted memberships
  // and excludes pending invitations by design — exactly what we need.
  // `membership.role` is "org:admin" for org admins (Clerk prefixes the
  // role with `org:` in v5).
  const { organization, membership } = useOrganization();

  const isOnlyAdmin = useMemo(() => {
    if (!organization || !membership) return false;
    if (membership.role !== "org:admin") return false;
    return organization.membersCount === 1;
  }, [organization, membership]);

  async function handleSignOut() {
    await clerk.signOut();
    navigate("/login", { replace: true });
  }

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <SectionHeader
          eyebrow="Settings · Account"
          title="Account"
          description="Profile, security, and organization preferences."
          action={
            <button
              type="button"
              className="btn-ghost btn-danger"
              onClick={() => void handleSignOut()}
            >
              Sign out
            </button>
          }
        />

        {/* S-082 — defense-in-depth visible warning. Renders regardless
            of whether the CSS hide rule lands; even if Clerk renames its
            selectors, this banner makes the intent unmistakable. */}
        {isOnlyAdmin && (
          <div
            className="team-banner"
            role="status"
            style={{ marginTop: 16 }}
          >
            You are the only admin of this workspace. Promote another
            teammate to admin in{" "}
            <Link to="/settings/team" className="settings-link">
              Settings → Team
            </Link>{" "}
            before you can leave or delete this organization.
          </div>
        )}

        {/* FEAT-087 / S-376 — the Leaving block. Copy is the prototype's,
            verbatim. The design's point is that the block states what happens
            to your pieces: the question anyone asks before leaving is "does my
            work go with me", and leaving it unanswered is what makes the
            control frightening rather than merely serious.

            The second sentence is not decoration either — it is the same
            last-admin rule S-082 already enforces by hiding Clerk's own
            controls, said out loud. The redesign's house rule is that wherever
            a control is unavailable the UI states why. */}
        <section className="acct-leaving">
          <span className="acct-leaving-label">Leaving</span>
          <p className="acct-leaving-body">
            Removing yourself from every workspace does not delete the pieces
            you made — they stay with the workspace. You cannot remove yourself
            from a workspace where you are the last admin.
          </p>
          <div className="acct-leaving-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => void handleSignOut()}
            >
              Sign out
            </button>
          </div>
        </section>

        {/* S-082 — injected CSS hides Clerk's leave/delete sections
            inside both the OrganizationSwitcher's Manage flyout and the
            inline OrganizationProfile. Scoped to this view so other
            screens (and other users with co-admins) get the full UX. */}
        {isOnlyAdmin && (
          <style
            // biome-ignore lint/security/noDangerouslySetInnerHtml: static CSS string, no user input
            dangerouslySetInnerHTML={{ __html: HIDE_DANGER_CSS }}
          />
        )}

        {tenantMe?.tenant_type && (
          <section style={{ marginTop: 16 }}>
            <h2
              className="panel-section-label"
              style={{ marginBottom: 8, fontSize: 13 }}
            >
              Plan
            </h2>
            <div
              style={{
                padding: 16,
                background: "var(--surface)",
                border: "1px solid var(--rule)",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span className="pill pill-review">
                {TENANT_TYPE_LABELS[tenantMe.tenant_type] ?? tenantMe.tenant_type}
              </span>
              <p style={{ margin: 0, fontSize: 12, color: "var(--muted-fg)" }}>
                Tenant type — managed by the platform operator.
              </p>
            </div>
          </section>
        )}

        <section style={{ marginTop: 16 }}>
          <h2
            className="panel-section-label"
            style={{ marginBottom: 8, fontSize: 13 }}
          >
            Organization
          </h2>
          <div
            style={{
              padding: 16,
              background: "var(--surface)",
              border: "1px solid var(--rule)",
              borderRadius: 8,
            }}
          >
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--muted-fg)" }}>
              Switch between agencies you belong to. Each agency is a
              separate Clerk organization mapped to its own CCM tenant.
            </p>
            <OrganizationSwitcher
              hidePersonal
              afterSelectOrganizationUrl="/"
              afterCreateOrganizationUrl="/onboarding"
            />
          </div>
        </section>

        <DateFormatSection />

        <section style={{ marginTop: 24 }}>
          <h2
            className="panel-section-label"
            style={{ marginBottom: 8, fontSize: 13 }}
          >
            Profile
          </h2>
          <UserProfile
            routing="hash"
            appearance={{
              elements: {
                rootBox: { width: "100%", minWidth: 0 },
                card: { boxShadow: "none", border: "1px solid var(--rule)", width: "100%", minWidth: 0 },
                navbar: { minWidth: 0 },
                scrollBox: { minWidth: 0 },
              },
            }}
          />
        </section>
      </div>
    </main>
  );
}

/**
 * FEAT-087 / S-363 — the timestamp-format setting.
 *
 * Three regional presets rather than a free-form locale picker, per the
 * design: each card renders a live sample of itself through the same
 * formatter the rest of the app uses, so the choice is made by looking at
 * the result instead of decoding "DD/MM vs MM/DD".
 */
function DateFormatSection() {
  const current = useDateFormat();
  const { setDateFormat, isSaving, error } = useSetDateFormat();

  // One fixed instant for all three samples — the design's own reference
  // moment, so the cards differ only by format, never by value.
  const sample = new Date(2026, 7, 28, 16, 20, 0);

  return (
    <section style={{ marginTop: 24 }}>
      <h2 className="panel-section-label" style={{ marginBottom: 8, fontSize: 13 }}>
        Date &amp; time format
      </h2>
      <div
        style={{
          padding: 16,
          background: "var(--surface)",
          border: "1px solid var(--rule)",
          borderRadius: 8,
        }}
      >
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--muted-fg)" }}>
          Applies everywhere a date or time is shown. Dates always read date
          first, then time; the region sets the field order and the clock.
        </p>
        <div style={{ display: "grid", gap: 8 }}>
          {DATE_FORMAT_SETTINGS.map((setting) => {
            const isCurrent = setting === current;
            return (
              <button
                key={setting}
                type="button"
                onClick={() => void setDateFormat(setting)}
                disabled={isSaving || isCurrent}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  borderRadius: 7,
                  border: `1px solid ${isCurrent ? "var(--accent)" : "var(--rule)"}`,
                  background: isCurrent ? "var(--today)" : "var(--surface)",
                  cursor: isCurrent || isSaving ? "default" : "pointer",
                }}
              >
                <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                  {DATE_FORMAT_LABELS[setting]}
                  {setting === DEFAULT_DATE_FORMAT && (
                    <span style={{ color: "var(--muted-fg)", fontSize: 11 }}> · default</span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--muted-fg)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatDateTime(sample, setting)}
                </span>
                {isCurrent && (
                  <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>
                    Selected
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {error && (
          <p className="settings-field-error" style={{ marginTop: 8 }}>
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
